import {
    ArrowLeft,
    Banknote,
    CheckCircle2,
    ChevronRight,
    FileText,
    Info,
    Layers,
    Minus,
    Pencil,
    Plus,
    Power,
    Save,
    Trash2,
    Upload,
    X,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    createPackage,
    deletePackage,
    getCommissionAll,
    getPackageById,
    getPackageCategories,
    updatePackage,
} from '../../../api/package.api';
import api from '../../../config/axios-conf';
import { PERMISSIONS } from '../../../config/permissions';
import { usePermissions } from '../../../hooks/usePermissions';
import { useToastContext } from '../../../contexts/ToastContext';
import usePackagePickerStore from '../../../stores/packagePickerStore';
import { formatCurrency } from '../../../utils/formatters';
import { compressImage } from '../../../utils/image';
import KeypadInput from '../../../components/common/KeypadInput';
import PackageItemPicker from './PackageItemPicker';
import styles from '../../../assets/scss/pages/PackageEditor.module.scss';

// Port từ app: screens/admin/PackageEditor.js
// Tạo / sửa / xem gói dịch vụ. Gói = bó nhiều dịch vụ + số buổi cố định.

export default function PackageEditor() {
    const { id } = useParams();
    const isEdit = Boolean(id);
    const navigate = useNavigate();

    const { hasPermission } = usePermissions();
    const canUpdatePackage = hasPermission(PERMISSIONS.SERVICE.UPDATE);
    const canCreatePackage = hasPermission(PERMISSIONS.SERVICE.CREATE);

    // viewMode=true: chỉ xem. Mặc định: tạo mới → false; mở từ list (có id) → true.
    const [viewMode, setViewMode] = useState(isEdit);

    const { showError, showSuccess } = useToastContext();

    const [loading, setLoading] = useState(isEdit);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');

    // Báo lỗi đồng thời: banner inline (đầu form) + toast trượt góc phải.
    const reportError = useCallback(
        (msg) => {
            setError(msg);
            showError(msg);
        },
        [showError],
    );

    const [form, setForm] = useState({
        code: '',
        name: '',
        urlImg: null,
        categoryId: null,
        totalSessions: '5',
        price: '',
        salePrice: '',
        validityMonths: '6',
        sellerCommissionPercent: '0',
        performerCommissionPercent: '0',
        description: '',
        procedureNote: '',
        isActive: true,
    });
    const [items, setItems] = useState([]);
    const [usedNumber, setUsedNumber] = useState(0);
    const hasCustomers = usedNumber > 0;

    const [categories, setCategories] = useState([]);
    const [commissions, setCommissions] = useState([]);

    const [showCategoryPicker, setShowCategoryPicker] = useState(false);
    const [showCommissionPicker, setShowCommissionPicker] = useState(false);
    const [commissionTarget, setCommissionTarget] = useState(null); // 'seller' | 'performer'
    const [showItemPicker, setShowItemPicker] = useState(false);

    const fileRef = useRef(null);
    const setPickerItems = usePackagePickerStore((s) => s.setItems);
    const clearPicker = usePackagePickerStore((s) => s.clear);

    const updateForm = (key, val) => setForm((prev) => ({ ...prev, [key]: val }));

    const fetchCategories = useCallback(async () => {
        try {
            const res = await getPackageCategories(false);
            setCategories(res?.data?.data || []);
        } catch {
            setCategories([]);
        }
    }, []);

    const fetchCommissions = useCallback(async () => {
        try {
            const res = await getCommissionAll();
            const list = res?.data?.commissions || [];
            setCommissions(list.filter((c) => c.type === 'percent'));
        } catch {
            setCommissions([]);
        }
    }, []);

    const fetchPackage = useCallback(async () => {
        if (!isEdit) return;
        try {
            setLoading(true);
            const res = await getPackageById(id);
            const pkg = res?.data?.data || res?.data;
            if (!pkg) return;
            setForm({
                code: pkg.code || '',
                name: pkg.name || '',
                urlImg: pkg.urlImg || null,
                categoryId: pkg.categoryId || null,
                totalSessions: String(pkg.totalSessions || 1),
                price: pkg.price ? String(Number(pkg.price)) : '',
                salePrice: pkg.salePrice ? String(Number(pkg.salePrice)) : '',
                validityMonths: pkg.validityMonths ? String(pkg.validityMonths) : '',
                sellerCommissionPercent: String(Number(pkg.sellerCommissionPercent || 0)),
                performerCommissionPercent: String(Number(pkg.performerCommissionPercent || 0)),
                description: pkg.description || '',
                procedureNote: pkg.procedureNote || '',
                isActive: !!pkg.isActive,
            });
            setItems(
                (pkg.items || []).map((it) => ({
                    name: it.name,
                    quantity: it.quantity || 1,
                    sourceServiceId: it.sourceServiceId || null,
                    isVirtual: !!it.isVirtual,
                    sortOrder: it.sortOrder || 0,
                })),
            );
            setUsedNumber(pkg.usedNumber || 0);
        } catch (err) {
            reportError(err?.response?.data?.message || 'Không tải được gói');
        } finally {
            setLoading(false);
        }
    }, [id, isEdit, reportError]);

    useEffect(() => {
        clearPicker();
        fetchCategories();
        fetchCommissions();
        fetchPackage();
    }, [clearPicker, fetchCategories, fetchCommissions, fetchPackage]);

    // Chặn quyền: tạo mới mà không có quyền tạo → quay lại danh sách (giống app).
    useEffect(() => {
        if (!isEdit && !canCreatePackage) {
            navigate('/admin/services?tab=lieutrinh', { replace: true });
        }
    }, [isEdit, canCreatePackage, navigate]);

    // Mở picker: nạp items hiện tại vào store
    const openItemPicker = () => {
        setPickerItems(items);
        setShowItemPicker(true);
    };

    // Đóng picker: đồng bộ items từ store về local
    const closeItemPicker = () => {
        const picked = usePackagePickerStore.getState().items;
        setItems(
            picked.map((it, idx) => ({
                name: it.name,
                quantity: it.quantity || 1,
                sourceServiceId: it.sourceServiceId || null,
                isVirtual: !!it.isVirtual,
                sortOrder: idx,
            })),
        );
        setShowItemPicker(false);
    };

    const handleImageSelect = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        try {
            setUploading(true);
            const compressed = await compressImage(file, { maxWidth: 800, quality: 0.6 });
            const fd = new FormData();
            fd.append('file', compressed);
            const res = await api.post('/upload/upload', fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
                timeout: 20000,
            });
            if (res.data?.url) updateForm('urlImg', res.data.url);
            else reportError('Upload ảnh không thành công');
        } catch {
            reportError('Upload ảnh thất bại');
        } finally {
            setUploading(false);
        }
    };

    const updateItemQty = (idx, qty) => {
        const num = Math.max(1, parseInt(qty, 10) || 1);
        setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, quantity: num } : it)));
    };

    const removeItem = (idx) => {
        setItems((prev) => prev.filter((_, i) => i !== idx));
    };

    const selectCommission = (com) => {
        if (commissionTarget === 'seller') updateForm('sellerCommissionPercent', String(com.value));
        else if (commissionTarget === 'performer')
            updateForm('performerCommissionPercent', String(com.value));
        setShowCommissionPicker(false);
        setCommissionTarget(null);
    };

    const validate = () => {
        if (!form.name.trim()) return 'Tên gói không được trống';
        if (!form.totalSessions || Number(form.totalSessions) < 1) return 'Tổng số buổi phải >= 1';
        if (!form.price || Number(form.price) <= 0) return 'Giá gói phải > 0';
        if (form.salePrice && Number(form.salePrice) >= Number(form.price))
            return 'Giá khuyến mãi phải nhỏ hơn giá gốc';
        if (items.length === 0) return 'Phải có ít nhất 1 dịch vụ trong gói';
        return null;
    };

    const buildBody = () => {
        // Chỉ include fields có giá trị (omit null/empty) — tránh class-validator reject.
        const body = {
            name: form.name.trim(),
            sellerCommissionPercent: Number(form.sellerCommissionPercent || 0),
            performerCommissionPercent: Number(form.performerCommissionPercent || 0),
            isActive: form.isActive,
        };
        if (form.urlImg) body.urlImg = form.urlImg;
        if (form.description?.trim()) body.description = form.description.trim();
        if (form.procedureNote?.trim()) body.procedureNote = form.procedureNote.trim();
        if (form.salePrice && Number(form.salePrice) > 0) body.salePrice = Number(form.salePrice);
        if (form.code?.trim()) body.code = form.code.trim();
        if (form.categoryId) body.categoryId = Number(form.categoryId);
        body.totalSessions = Number(form.totalSessions);
        body.price = Number(form.price);
        if (form.validityMonths && Number(form.validityMonths) > 0) {
            body.validityMonths = Number(form.validityMonths);
        }
        body.items = items.map((it, idx) => {
            const item = {
                name: it.name,
                quantity: Number(it.quantity) || 1,
                isVirtual: !!it.isVirtual,
                sortOrder: idx,
            };
            if (it.sourceServiceId) item.sourceServiceId = Number(it.sourceServiceId);
            return item;
        });
        return body;
    };

    const handleSave = async () => {
        if (isEdit && !canUpdatePackage) {
            reportError('Bạn không có quyền sửa gói dịch vụ');
            return;
        }
        if (!isEdit && !canCreatePackage) {
            reportError('Bạn không có quyền tạo gói dịch vụ');
            return;
        }
        const err = validate();
        if (err) {
            reportError(err);
            return;
        }
        try {
            setSaving(true);
            if (isEdit) await updatePackage(id, buildBody());
            else await createPackage(buildBody());
            clearPicker();
            showSuccess(isEdit ? 'Đã cập nhật gói dịch vụ' : 'Đã tạo gói dịch vụ');
            navigate('/admin/services?tab=lieutrinh');
        } catch (e) {
            const respData = e?.response?.data;
            const msg = Array.isArray(respData?.message)
                ? respData.message.join('\n')
                : respData?.message || e?.message || 'Lưu thất bại';
            reportError(msg);
        } finally {
            setSaving(false);
        }
    };

    const [confirmDelete, setConfirmDelete] = useState(false);
    const handleDelete = async () => {
        try {
            await deletePackage(id);
            clearPicker();
            showSuccess('Đã xoá gói dịch vụ');
            navigate('/admin/services?tab=lieutrinh');
        } catch (e) {
            reportError(e?.response?.data?.message || 'Xoá thất bại');
            setConfirmDelete(false);
        }
    };

    const selectedCategory = categories.find((c) => c.id === form.categoryId);

    // Không có quyền tạo → không render form (effect ở trên đã điều hướng đi).
    if (!isEdit && !canCreatePackage) return null;

    if (loading) {
        return (
            <div className={styles.pkgEditor}>
                <div className={styles.loading}>
                    <div className={styles.spinner} /> Đang tải gói dịch vụ...
                </div>
            </div>
        );
    }

    const title = !isEdit ? 'Tạo gói dịch vụ' : viewMode ? 'Chi tiết gói dịch vụ' : 'Sửa gói dịch vụ';

    return (
        <div className={styles.pkgEditor}>
            {/* Header */}
            <div className={styles.formHeader}>
                <div className={styles.formHeaderLeft}>
                    <button className={styles.backBtn} onClick={() => navigate(-1)}>
                        <ArrowLeft />
                    </button>
                    <h1 className={styles.formTitle}>{title}</h1>
                </div>
                {viewMode ? (
                    <button
                        className={styles.submitBtn}
                        onClick={() => {
                            if (!canUpdatePackage) {
                                reportError('Bạn không có quyền sửa gói dịch vụ');
                                return;
                            }
                            setViewMode(false);
                        }}
                    >
                        <Pencil size={16} /> Sửa
                    </button>
                ) : (
                    <button className={styles.submitBtn} onClick={handleSave} disabled={saving}>
                        <Save size={16} /> {saving ? 'Đang lưu...' : isEdit ? 'Cập nhật' : 'Tạo gói'}
                    </button>
                )}
            </div>

            {error && <div className={styles.errorMsg}>{error}</div>}

            {hasCustomers && !viewMode && (
                <div className={styles.lockNote}>
                    <Info size={16} />
                    <span>
                        Gói đã có {usedNumber} khách mua. Chỉnh sửa chỉ áp dụng cho khách mua sau — khách
                        đã mua vẫn giữ giá/buổi/dịch vụ cũ.
                    </span>
                </div>
            )}

            {/* ====== VIEW MODE ====== */}
            {viewMode ? (
                <div className={styles.viewWrap}>
                    <div className={styles.viewHero}>
                        <div className={styles.viewHeroImg}>
                            {form.urlImg ? <img src={form.urlImg} alt={form.name} /> : <Layers size={36} />}
                        </div>
                        <div className={styles.viewHeroBody}>
                            <h2 className={styles.viewHeroName}>{form.name}</h2>
                            {form.code && <span className={styles.viewHeroCode}>Mã: {form.code}</span>}
                            <span
                                className={`${styles.viewStatus} ${form.isActive ? styles.viewStatusOn : styles.viewStatusOff}`}
                            >
                                ● {form.isActive ? 'Đang bán' : 'Tạm tắt'}
                            </span>
                        </div>
                    </div>

                    <div className={styles.card}>
                        <div className={styles.sectionLabel}>
                            <Info size={16} /> Thông tin gói
                        </div>
                        <div className={styles.infoRow}>
                            <span className={styles.infoLabel}>Số buổi</span>
                            <span className={styles.infoValue}>{form.totalSessions} buổi</span>
                        </div>
                        <div className={styles.infoRow}>
                            <span className={styles.infoLabel}>Hạn sử dụng</span>
                            <span className={styles.infoValue}>
                                {form.validityMonths ? `${form.validityMonths} tháng` : 'Vô hạn'}
                            </span>
                        </div>
                        <div className={styles.infoRow}>
                            <span className={styles.infoLabel}>Danh mục</span>
                            <span className={styles.infoValue}>{selectedCategory?.name || 'Không có'}</span>
                        </div>
                        <div className={styles.infoDivider} />
                        <div className={styles.infoRow}>
                            <span className={styles.infoLabel}>Giá gốc</span>
                            <span className={form.salePrice ? styles.priceStrike : styles.priceMain}>
                                {formatCurrency(form.price || 0)}
                            </span>
                        </div>
                        {form.salePrice && (
                            <div className={styles.infoRow}>
                                <span className={styles.infoLabel}>Giá khuyến mãi</span>
                                <span className={styles.priceMain}>{formatCurrency(form.salePrice)}</span>
                            </div>
                        )}
                        {hasCustomers && (
                            <div className={styles.infoRow}>
                                <span className={styles.infoLabel}>Đã bán</span>
                                <span className={styles.infoValueAccent}>{usedNumber} khách</span>
                            </div>
                        )}
                    </div>

                    <div className={styles.card}>
                        <div className={styles.sectionLabel}>
                            <Banknote size={16} /> Hoa hồng nhân viên
                        </div>
                        <div className={styles.infoRow}>
                            <span className={styles.infoLabel}>NV bán gói</span>
                            <span className={styles.infoValue}>
                                {Number(form.sellerCommissionPercent) > 0
                                    ? `${form.sellerCommissionPercent}%`
                                    : '—'}
                            </span>
                        </div>
                        <div className={styles.infoRow}>
                            <span className={styles.infoLabel}>NV làm buổi</span>
                            <span className={styles.infoValue}>
                                {Number(form.performerCommissionPercent) > 0
                                    ? `${form.performerCommissionPercent}%`
                                    : '—'}
                            </span>
                        </div>
                    </div>

                    <div className={styles.card}>
                        <div className={styles.sectionLabel}>
                            <Layers size={16} /> Dịch vụ trong gói ({items.length})
                        </div>
                        {items.length === 0 ? (
                            <div className={styles.emptyItems}>Không có dịch vụ nào</div>
                        ) : (
                            items.map((it, idx) => (
                                <div key={idx} className={styles.viewItemRow}>
                                    <CheckCircle2 size={18} className={styles.viewItemIcon} />
                                    <span className={styles.viewItemName}>
                                        {it.name}
                                        {it.isVirtual && <span className={styles.virtualTag}> (thêm)</span>}
                                    </span>
                                    <span className={styles.viewItemQty}>×{it.quantity}/buổi</span>
                                </div>
                            ))
                        )}
                    </div>

                    {(form.description || form.procedureNote) && (
                        <div className={styles.card}>
                            <div className={styles.sectionLabel}>
                                <FileText size={16} /> Mô tả & Quy trình
                            </div>
                            {form.description && (
                                <>
                                    <div className={styles.viewSubLabel}>Mô tả ngắn (cho khách)</div>
                                    <p className={styles.viewParagraph}>{form.description}</p>
                                </>
                            )}
                            {form.procedureNote && (
                                <>
                                    <div className={styles.viewSubLabel}>Quy trình theo buổi (NV thấy)</div>
                                    <p className={styles.viewParagraph}>{form.procedureNote}</p>
                                </>
                            )}
                        </div>
                    )}
                </div>
            ) : (
                /* ====== EDIT / CREATE MODE ====== */
                <div className={styles.formWrap}>
                    {/* Thông tin cơ bản */}
                    <div className={styles.card}>
                        <div className={styles.sectionLabel}>
                            <Info size={16} /> Thông tin cơ bản
                        </div>
                        <div className={styles.basicRow}>
                            <div className={styles.imageUpload} onClick={() => fileRef.current?.click()}>
                                {form.urlImg ? (
                                    <img src={form.urlImg} alt="" className={styles.imagePreview} />
                                ) : (
                                    <div className={styles.imagePlaceholder}>
                                        {uploading ? <span>...</span> : <><Upload size={22} /><span>Chọn ảnh</span></>}
                                    </div>
                                )}
                                <input
                                    ref={fileRef}
                                    type="file"
                                    accept="image/*"
                                    hidden
                                    onChange={handleImageSelect}
                                />
                            </div>
                            <div className={styles.basicFields}>
                                <label className={styles.formLabel}>
                                    Tên gói <span className={styles.req}>*</span>
                                </label>
                                <input
                                    className={styles.formInput}
                                    value={form.name}
                                    onChange={(e) => updateForm('name', e.target.value)}
                                    placeholder="Ví dụ: Combo trắng da 6 buổi"
                                />
                                <label className={styles.formLabel}>Mã gói</label>
                                <input
                                    className={styles.formInput}
                                    value={form.code}
                                    onChange={(e) => updateForm('code', e.target.value)}
                                    placeholder="Để trống = tự sinh"
                                />
                            </div>
                        </div>

                        <label className={styles.formLabel}>Danh mục</label>
                        <button className={styles.pickerRow} onClick={() => setShowCategoryPicker(true)}>
                            <span className={selectedCategory ? styles.pickerText : styles.pickerPlaceholder}>
                                {selectedCategory?.name || 'Chọn danh mục (tuỳ chọn)'}
                            </span>
                            <ChevronRight size={18} />
                        </button>

                        <div className={styles.fieldRow}>
                            <div className={styles.fieldCol}>
                                <label className={styles.formLabel}>
                                    Tổng buổi <span className={styles.req}>*</span>
                                </label>
                                <KeypadInput
                                    className={styles.formInput}
                                    mode="integer"
                                    value={form.totalSessions}
                                    onChange={(v) => updateForm('totalSessions', v)}
                                    placeholder="6"
                                    title="Tổng số buổi"
                                    suffix="buổi"
                                />
                            </div>
                            <div className={styles.fieldCol}>
                                <label className={styles.formLabel}>HSD (tháng)</label>
                                <KeypadInput
                                    className={styles.formInput}
                                    mode="integer"
                                    value={form.validityMonths}
                                    onChange={(v) => updateForm('validityMonths', v)}
                                    placeholder="Vô hạn"
                                    title="Hạn sử dụng (tháng)"
                                    suffix="tháng"
                                />
                            </div>
                        </div>

                        <div className={styles.fieldRow}>
                            <div className={styles.fieldCol}>
                                <label className={styles.formLabel}>
                                    Giá gói <span className={styles.req}>*</span>
                                </label>
                                <KeypadInput
                                    className={styles.formInput}
                                    mode="money"
                                    value={form.price}
                                    onChange={(v) => updateForm('price', v)}
                                    placeholder="0"
                                    title="Giá gói"
                                    suffix="đ"
                                />
                            </div>
                            <div className={styles.fieldCol}>
                                <label className={styles.formLabel}>Giá khuyến mãi</label>
                                <KeypadInput
                                    className={styles.formInput}
                                    mode="money"
                                    value={form.salePrice}
                                    onChange={(v) => updateForm('salePrice', v)}
                                    placeholder="Không có"
                                    title="Giá khuyến mãi"
                                    suffix="đ"
                                />
                            </div>
                        </div>

                        <div className={styles.switchRow}>
                            <div className={styles.switchLabel}>
                                <div className={styles.switchTitle}>
                                    <Power size={14} /> Đang bán
                                </div>
                                <span className={styles.fieldHint}>Tắt để ẩn khỏi danh sách bán hàng</span>
                            </div>
                            <label className={styles.toggle}>
                                <input
                                    type="checkbox"
                                    checked={form.isActive}
                                    onChange={(e) => updateForm('isActive', e.target.checked)}
                                />
                                <span className={styles.toggleSlider} />
                            </label>
                        </div>
                    </div>

                    {/* Hoa hồng */}
                    <div className={styles.card}>
                        <div className={styles.sectionLabel}>
                            <Banknote size={16} /> Hoa hồng nhân viên
                        </div>
                        <div className={styles.fieldRow}>
                            <div className={styles.fieldCol}>
                                <label className={styles.formLabel}>NV bán gói</label>
                                <button
                                    className={styles.pickerRow}
                                    onClick={() => {
                                        if (commissions.length === 0) {
                                            reportError('Vui lòng tạo hoa hồng (loại %) trước');
                                            return;
                                        }
                                        setCommissionTarget('seller');
                                        setShowCommissionPicker(true);
                                    }}
                                >
                                    <span className={styles.pickerText}>
                                        {Number(form.sellerCommissionPercent) > 0
                                            ? `${form.sellerCommissionPercent}%`
                                            : 'Chưa chọn'}
                                    </span>
                                    <ChevronRight size={18} />
                                </button>
                            </div>
                            <div className={styles.fieldCol}>
                                <label className={styles.formLabel}>NV làm buổi</label>
                                <button
                                    className={styles.pickerRow}
                                    onClick={() => {
                                        if (commissions.length === 0) {
                                            reportError('Vui lòng tạo hoa hồng (loại %) trước');
                                            return;
                                        }
                                        setCommissionTarget('performer');
                                        setShowCommissionPicker(true);
                                    }}
                                >
                                    <span className={styles.pickerText}>
                                        {Number(form.performerCommissionPercent) > 0
                                            ? `${form.performerCommissionPercent}%`
                                            : 'Chưa chọn'}
                                    </span>
                                    <ChevronRight size={18} />
                                </button>
                            </div>
                        </div>
                        <span className={styles.fieldHint}>
                            Chỉ liệt kê hoa hồng loại %. Tạo mới ở mục Tạo hoa hồng.
                        </span>
                    </div>

                    {/* Dịch vụ trong gói */}
                    <div className={styles.card}>
                        <div className={styles.sectionLabel}>
                            <Layers size={16} /> Dịch vụ trong gói ({items.length})
                        </div>
                        {items.length === 0 ? (
                            <div className={styles.emptyItems}>Chưa có dịch vụ nào</div>
                        ) : (
                            items.map((it, idx) => (
                                <div key={idx} className={styles.itemRow}>
                                    <CheckCircle2 size={20} className={styles.itemIcon} />
                                    <div className={styles.itemBody}>
                                        <span className={styles.itemName}>
                                            {it.name}
                                            {it.isVirtual && <span className={styles.virtualTag}> (thêm)</span>}
                                        </span>
                                        <div className={styles.itemQtyRow}>
                                            <span className={styles.itemQtyLabel}>SL/buổi:</span>
                                            <div className={styles.pkgQtyBox}>
                                                <button
                                                    className={styles.pkgQtyBtn}
                                                    onClick={() => updateItemQty(idx, it.quantity - 1)}
                                                >
                                                    <Minus size={14} />
                                                </button>
                                                <input
                                                    className={styles.pkgQtyInput}
                                                    value={String(it.quantity)}
                                                    onChange={(e) =>
                                                        updateItemQty(idx, e.target.value.replace(/\D/g, ''))
                                                    }
                                                />
                                                <button
                                                    className={styles.pkgQtyBtn}
                                                    onClick={() => updateItemQty(idx, it.quantity + 1)}
                                                >
                                                    <Plus size={14} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                    <button className={styles.pkgRemoveBtn} onClick={() => removeItem(idx)}>
                                        <Trash2 size={18} />
                                    </button>
                                </div>
                            ))
                        )}
                        <button className={styles.addItemBtn} onClick={openItemPicker}>
                            <Plus size={18} /> Thêm dịch vụ
                        </button>
                    </div>

                    {/* Mô tả & Quy trình */}
                    <div className={styles.card}>
                        <div className={styles.sectionLabel}>
                            <FileText size={16} /> Mô tả & Quy trình
                        </div>
                        <label className={styles.formLabel}>Mô tả ngắn (POS / phiếu in)</label>
                        <textarea
                            className={styles.formTextarea}
                            value={form.description}
                            onChange={(e) => updateForm('description', e.target.value)}
                            placeholder="Gói dịch vụ trắng da chuyên sâu..."
                        />
                        <label className={styles.formLabel}>Quy trình theo buổi (NV thấy)</label>
                        <textarea
                            className={styles.formTextarea}
                            value={form.procedureNote}
                            onChange={(e) => updateForm('procedureNote', e.target.value)}
                            placeholder={'Buổi 1-2: tẩy tế bào chết\nBuổi 3: peel da\nBuổi 4-5: tái tạo'}
                        />
                    </div>

                    {/* Footer actions */}
                    <div className={styles.formFooter}>
                        {isEdit && (
                            <button
                                className={styles.cancelBtn}
                                onClick={() => {
                                    setViewMode(true);
                                    fetchPackage();
                                }}
                            >
                                Huỷ
                            </button>
                        )}
                        {isEdit && (
                            <button className={styles.dangerBtn} onClick={() => setConfirmDelete(true)}>
                                <Trash2 size={16} /> Xoá
                            </button>
                        )}
                        <button className={styles.footerSubmitBtn} onClick={handleSave} disabled={saving}>
                            <Save size={16} /> {saving ? 'Đang lưu...' : isEdit ? 'Cập nhật' : 'Tạo gói mới'}
                        </button>
                    </div>
                </div>
            )}

            {/* Item picker modal */}
            <PackageItemPicker open={showItemPicker} onClose={closeItemPicker} />

            {/* Category picker */}
            {showCategoryPicker && (
                <div className={styles.modalOverlay} onClick={() => setShowCategoryPicker(false)}>
                    <div className={styles.modalBox} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h3 className={styles.modalTitle}>Chọn danh mục</h3>
                            <button className={styles.modalClose} onClick={() => setShowCategoryPicker(false)}>
                                <X size={20} />
                            </button>
                        </div>
                        <div className={styles.pickerList}>
                            <button
                                className={styles.pickerItem}
                                onClick={() => {
                                    updateForm('categoryId', null);
                                    setShowCategoryPicker(false);
                                }}
                            >
                                (Không chọn)
                            </button>
                            {categories.map((cat) => (
                                <button
                                    key={cat.id}
                                    className={styles.pickerItem}
                                    onClick={() => {
                                        updateForm('categoryId', cat.id);
                                        setShowCategoryPicker(false);
                                    }}
                                >
                                    {cat.name}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Commission picker */}
            {showCommissionPicker && (
                <div className={styles.modalOverlay} onClick={() => setShowCommissionPicker(false)}>
                    <div className={styles.modalBox} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h3 className={styles.modalTitle}>
                                Chọn hoa hồng {commissionTarget === 'seller' ? 'NV bán' : 'NV làm buổi'}
                            </h3>
                            <button className={styles.modalClose} onClick={() => setShowCommissionPicker(false)}>
                                <X size={20} />
                            </button>
                        </div>
                        <div className={styles.pickerList}>
                            {commissions.map((com) => (
                                <button
                                    key={com.id}
                                    className={styles.pickerItem}
                                    onClick={() => selectCommission(com)}
                                >
                                    <span>{com.name}</span>
                                    <span className={styles.pickerItemValue}>{com.value}%</span>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Confirm delete */}
            {confirmDelete && (
                <div className={styles.modalOverlay} onClick={() => setConfirmDelete(false)}>
                    <div className={styles.modalBox} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h3 className={styles.modalTitle}>Xoá gói dịch vụ</h3>
                            <button className={styles.modalClose} onClick={() => setConfirmDelete(false)}>
                                <X size={20} />
                            </button>
                        </div>
                        <p className={styles.confirmText}>
                            Bạn có chắc chắn muốn xoá gói <strong>{form.name}</strong>?
                        </p>
                        <div className={styles.modalFooter}>
                            <button className={styles.cancelBtn} onClick={() => setConfirmDelete(false)}>
                                Huỷ
                            </button>
                            <button className={styles.dangerBtn} onClick={handleDelete}>
                                Xoá
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
